import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import {
  Mail,
  MessageCircle,
  Trash2,
  X,
  Send,
  ExternalLink,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Clock,
  ArrowDownLeft,
  ArrowUpRight,
  StickyNote,
  UserCheck,
  ShieldCheck,
  Pencil,
  Save,
  FileText,
  Download,
  PanelRightClose,
  Trophy,
  ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import ScoreIndicator from "@/components/ScoreIndicator";
import CandidateInterviewsPanel from "@/components/CandidateInterviewsPanel";
import CandidateAssessmentsPanel from "@/components/CandidateAssessmentsPanel";
import CandidateLinkedInTab from "@/components/CandidateLinkedInTab";
import HireCandidateDialog from "@/components/HireCandidateDialog";
import ScheduleInterviewDialog from "@/components/ScheduleInterviewDialog";
import InterviewSuccessDialog from "@/components/InterviewSuccessDialog";
import CandidateAdditionalInfoDialog from "@/components/CandidateAdditionalInfoDialog";

// Fallback list used only while the registry is loading. The real options
// are fetched from `GET /registry/statuses` on mount.
const FALLBACK_STATUS_OPTIONS = [
  { value: "pendente", label: "Pendente" },
  { value: "analisado", label: "Analisado" },
  { value: "contatado", label: "Contatado" },
  { value: "em_entrevista", label: "Em entrevista" },
  { value: "aprovado", label: "Aprovado" },
  { value: "rejeitado", label: "Rejeitado" },
];

const CandidateModal = ({ candidate, job, onClose, onUpdated, onDeleted }) => {
  const { can, user: me } = useAuth();
  const [tab, setTab] = useState("analise"); // analise | email | whatsapp | notas | gravacoes | agendar | historico
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [hireDialogOpen, setHireDialogOpen] = useState(false);
  const [scheduleSuccess, setScheduleSuccess] = useState(null);
  const [additionalInfoOpen, setAdditionalInfoOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [waMessage, setWaMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [contactDraft, setContactDraft] = useState({
    name: candidate?.name || "",
    email: candidate?.email || "",
    phone: candidate?.phone || "",
  });

  // PDF viewer state (side-by-side embedded viewer)
  const [showPdf, setShowPdf] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");

  // WhatsApp templates state (Meta-approved, read-only previews)

  // Pipeline statuses (registry-driven, replaces the hard-coded list).
  const [statusOptions, setStatusOptions] = useState(FALLBACK_STATUS_OPTIONS);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/registry/statuses`)
      .then((res) => {
        if (cancelled) return;
        const opts = (res.data || [])
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((s) => ({ value: s.key, label: s.label }));
        if (opts.length) setStatusOptions(opts);
      })
      .catch(() => {
        /* Keep fallback list silently */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const canNote = can("candidates", "note");
  const canAssign = can("candidates", "assign");
  const canSendEmail = can("candidates", "send_email");
  const canSendWA = can("candidates", "send_whatsapp");
  const canDelete = can("candidates", "delete");

  // Sync contact draft whenever candidate prop changes (different candidate
  // selected) — keeps the edit form in lockstep without preserving stale text.
  useEffect(() => {
    setContactDraft({
      name: candidate?.name || "",
      email: candidate?.email || "",
      phone: candidate?.phone || "",
    });
    setEditingContact(false);
  }, [candidate?.id]);

  // ── Testes obrigatórios pendentes ──────────────────────────────────
  // Compara `job.required_assessments` (configurado na vaga) com os
  // assessments ATIVOS do candidato (pending/in_progress/completed).
  // Quando há diff E o candidato está em "em_entrevista", oferece o
  // botão "Aplicar agora" caso o trigger automático tenha falhado.
  const [activeAssessments, setActiveAssessments] = useState([]);
  const [applyingRequired, setApplyingRequired] = useState(false);

  const refreshActiveAssessments = async () => {
    if (!candidate?.id) return;
    try {
      const res = await axios.get(`${API}/candidates/${candidate.id}/assessments`);
      setActiveAssessments(res.data || []);
    } catch {
      setActiveAssessments([]);
    }
  };

  useEffect(() => {
    refreshActiveAssessments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate?.id]);

  const missingRequiredTestIds = useMemo(() => {
    const required = job?.required_assessments || [];
    if (required.length === 0) return [];
    const activeIds = new Set(
      (activeAssessments || [])
        .filter((a) => ["pending", "in_progress", "completed"].includes(a.status))
        .map((a) => a.test_id),
    );
    return required.filter((tid) => !activeIds.has(tid));
  }, [job?.required_assessments, activeAssessments]);

  const handleApplyRequiredNow = async () => {
    setApplyingRequired(true);
    try {
      const res = await axios.post(
        `${API}/candidates/${candidate.id}/apply-required-assessments`,
        {},
      );
      const created = res.data.created || [];
      const skipped = res.data.skipped || [];
      const via = res.data.sent_via || "none";
      if (created.length > 0) {
        const channelTxt =
          via === "whatsapp" ? "via WhatsApp"
          : via === "email" ? "via E-mail"
          : "(sem notificação ao candidato — verifique SMTP)";
        toast.success(
          `${created.length} teste${created.length > 1 ? "s" : ""} aplicado${created.length > 1 ? "s" : ""} ${channelTxt}`,
        );
      } else if (skipped.length > 0) {
        toast.info("Todos os testes obrigatórios já estavam aplicados.");
      } else {
        toast.info("Nenhuma ação executada.");
      }
      await refreshActiveAssessments();
      setTab("avaliacoes");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao aplicar testes obrigatórios");
    } finally {
      setApplyingRequired(false);
    }
  };

  const handleSaveContact = async () => {
    setSavingContact(true);
    try {
      const res = await axios.patch(
        `${API}/candidates/${candidate.id}/contact`,
        {
          name: contactDraft.name.trim() || null,
          email: contactDraft.email.trim() === "" ? null : contactDraft.email.trim(),
          phone: contactDraft.phone.trim() === "" ? null : contactDraft.phone.trim(),
        }
      );
      onUpdated(res.data);
      setContactDraft({
        name: res.data.name || "",
        email: res.data.email || "",
        phone: res.data.phone || "",
      });
      setEditingContact(false);
      toast.success("Contato atualizado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar contato");
    } finally {
      setSavingContact(false);
    }
  };

  // iter77: change candidate's preferred channel (auto / whatsapp / linkedin)
  const handlePreferredChannelChange = async (newChannel) => {
    try {
      const res = await axios.patch(
        `${API}/candidates/${candidate.id}/contact`,
        { preferred_channel: newChannel },
      );
      onUpdated(res.data);
      const label = {
        auto: "Auto (multi-canal)",
        whatsapp: "Somente WhatsApp",
        linkedin: "Somente LinkedIn",
      }[newChannel] || newChannel;
      toast.success(`Canal preferido: ${label}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao alterar canal");
    }
  };

  const handleCancelEditContact = () => {
    setContactDraft({
      name: candidate?.name || "",
      email: candidate?.email || "",
      phone: candidate?.phone || "",
    });
    setEditingContact(false);
  };

  // ===== PDF viewer (side-by-side embedded) =====
  // We request a short-lived signed token (5 min) from the backend and embed
  // the PDF as a plain iframe src URL (?t=token). This avoids blob URLs which
  // are blocked by Edge/Chrome when the page is inside a nested iframe (e.g.
  // the Replit preview proxy).
  const loadPdfPreviewUrl = async () => {
    setPdfLoading(true);
    setPdfError("");
    try {
      const res = await axios.get(
        `${API}/candidates/${candidate.id}/resume-pdf-token`
      );
      const token = res.data?.token;
      if (!token) throw new Error("Token ausente na resposta");
      setPdfPreviewUrl(
        `${API}/candidates/${candidate.id}/resume.pdf?t=${encodeURIComponent(token)}`
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setPdfError(
        typeof detail === "string"
          ? detail
          : "Não foi possível carregar o PDF."
      );
    } finally {
      setPdfLoading(false);
    }
  };

  const handleTogglePdf = () => {
    if (showPdf) {
      setShowPdf(false);
      return;
    }
    setShowPdf(true);
    if (!pdfPreviewUrl) loadPdfPreviewUrl();
  };

  const handleDownloadPdf = async () => {
    try {
      const res = await axios.get(
        `${API}/candidates/${candidate.id}/resume.pdf?download=1`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const safeName =
        (candidate.name || candidate.filename || "curriculo")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^A-Za-z0-9_-]+/g, "_")
          .slice(0, 60) || "curriculo";
      const a = document.createElement("a");
      a.href = url;
      a.download = `Curriculo_${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Defer revoke a tick so the browser can start the download
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Download iniciado");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Não foi possível baixar o PDF"
      );
    }
  };

  const [downloadingAdditionalInfo, setDownloadingAdditionalInfo] = useState(false);

  const handleDownloadAdditionalInfoPdf = async () => {
    setDownloadingAdditionalInfo(true);
    try {
      const res = await axios.get(
        `${API}/candidates/${candidate.id}/additional-info.pdf`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const safeName =
        (candidate.name || "candidato")
          .normalize("NFD")
          .replace(/[̀-ͯ]/g, "")
          .replace(/[^A-Za-z0-9_-]+/g, "_")
          .slice(0, 60) || "candidato";
      const a = document.createElement("a");
      a.href = url;
      a.download = `Dados_Complementares_${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Download iniciado");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Não consegui gerar o PDF de dados complementares."
      );
    } finally {
      setDownloadingAdditionalInfo(false);
    }
  };

  // Reset PDF state when the modal switches to a different candidate
  useEffect(() => {
    setPdfPreviewUrl(null);
    setPdfBlobUrl(null);
    setShowPdf(false);
    setPdfError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate?.id]);

  const loadNotes = async () => {
    setLoadingNotes(true);
    try {
      const res = await axios.get(`${API}/candidates/${candidate.id}/notes`);
      setNotes(res.data || []);
    } catch (err) {
      // Silent on UX (notes panel can stay empty) but log to console for debugging
      console.warn("Failed to load candidate notes:", err);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleSaveNote = async () => {
    if (!newNote.trim()) {
      toast.error("Escreva o texto da nota");
      return;
    }
    setSavingNote(true);
    try {
      await axios.post(`${API}/candidates/${candidate.id}/notes`, {
        body: newNote.trim(),
      });
      setNewNote("");
      toast.success("Nota registrada");
      loadNotes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar nota");
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Excluir esta nota?")) return;
    try {
      await axios.delete(`${API}/candidates/${candidate.id}/notes/${noteId}`);
      toast.success("Nota excluída");
      loadNotes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir");
    }
  };

  const handleAssignManager = async () => {
    if (!job?.manager_id) {
      toast.error("Defina um gestor responsável na vaga primeiro.");
      return;
    }
    if (!window.confirm(`Enviar este candidato para o gestor ${job.manager_name}?`)) return;
    setAssigning(true);
    try {
      const res = await axios.post(`${API}/candidates/${candidate.id}/assign-manager`);
      onUpdated(res.data);
      toast.success(`Candidato enviado para ${res.data.assigned_manager_name}. E-mail disparado.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível enviar para o gestor");
    } finally {
      setAssigning(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/notifications/preview/${candidate.id}`);
        setPreview(res.data);
        setEmailSubject(res.data.email_subject);
        setEmailBody(res.data.email_body);
        setWaMessage(res.data.whatsapp_message);
      } catch {
        // silent
      }
    };
    load();
  }, [candidate.id]);

  // Fetch the company's approved Meta templates when the WhatsApp tab is
  // opened. Cached for the lifetime of the modal.
  // WhatsApp templates via Twilio were removed; sending goes via Synkly.
  // No template pre-loading needed — backend generates the message body.

  const renderTemplateBody = (body) => {
    if (!body) return "";
    return body.replace(/\{\{(\d+)\}\}/g, (_, k) =>
      k !== undefined ? `{{${k}}}` : `{{${k}}}`
    );
  };

  const renderedWaBody = ""
    ? renderTemplateBody("")
    : "";

  const loadTimeline = async () => {
    setLoadingTimeline(true);
    try {
      const res = await axios.get(`${API}/candidates/${candidate.id}/timeline`);
      setTimeline(res.data || []);
    } catch {
      // silent
    } finally {
      setLoadingTimeline(false);
    }
  };

  useEffect(() => {
    if (tab === "historico") loadTimeline();
    if (tab === "notas") loadNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, candidate.id]);

  const handleReanalyze = async () => {
    setReanalyzing(true);
    try {
      const res = await axios.post(`${API}/candidates/${candidate.id}/reanalyze`);
      onUpdated(res.data);
      toast.success("Reanálise iniciada — atualize em alguns segundos.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível reanalisar");
    } finally {
      setReanalyzing(false);
    }
  };

  const handleSendEmail = async () => {
    if (!candidate.email) {
      toast.error("Candidato não possui e-mail extraído do currículo");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/notifications/email`, {
        candidate_id: candidate.id,
        subject: emailSubject,
        body: emailBody,
      });
      toast.success(res.data.message || "E-mail enviado");
      onUpdated({ ...candidate, email_sent: true, status: "contatado" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar e-mail");
    } finally {
      setSending(false);
    }
  };

  const handleSendWhatsapp = async () => {
    if (!candidate.phone) {
      toast.error("Candidato não possui telefone extraído");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/notifications/whatsapp`, {
        candidate_id: candidate.id,
        message: waMessage || preview?.whatsapp_message || "",
      });
      toast.success(res.data.message || "Mensagem WhatsApp enviada com sucesso.");
      onUpdated({ ...candidate, whatsapp_sent: true, status: "contatado" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const errorMsg =
        Array.isArray(detail)
          ? detail.map((e) => e?.msg || JSON.stringify(e)).join("; ")
          : typeof detail === "string"
          ? detail
          : "Erro ao enviar WhatsApp";
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setStatusUpdating(true);
    try {
      const res = await axios.patch(`${API}/candidates/${candidate.id}/status`, {
        status: newStatus,
      });
      onUpdated(res.data);
      toast.success("Status atualizado");
    } catch {
      toast.error("Erro ao atualizar status");
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Excluir este candidato?")) return;
    try {
      await axios.delete(`${API}/candidates/${candidate.id}`);
      toast.success("Candidato excluído");
      onDeleted(candidate.id);
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="candidate-modal"
        className={`p-0 gap-0 overflow-hidden transition-all duration-200 ${
          showPdf
            ? "max-w-[1280px] w-[96vw] h-[92vh]"
            : "max-w-3xl max-h-[90vh]"
        }`}
      >
        <div
          className={
            showPdf
              ? "flex h-[92vh]"
              : "max-h-[90vh] overflow-y-auto"
          }
        >
          <div
            className={
              showPdf
                ? "flex-1 min-w-0 border-r border-slate-200 overflow-y-auto"
                : "w-full"
            }
          >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <DialogTitle className="heading text-2xl font-bold text-slate-950 leading-tight">
                {candidate.name || candidate.filename}
              </DialogTitle>
              {!editingContact ? (
                <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-600">
                  {candidate.email ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5" />
                      {candidate.email}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-amber-700 italic">
                      <Mail className="w-3.5 h-3.5" />
                      Sem e-mail no currículo
                    </span>
                  )}
                  {candidate.phone ? (
                    <span className="inline-flex items-center gap-1.5">
                      <MessageCircle className="w-3.5 h-3.5" />
                      {candidate.phone}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-amber-700 italic">
                      <MessageCircle className="w-3.5 h-3.5" />
                      Sem telefone no currículo
                    </span>
                  )}
                  {candidate.experience_years > 0 && (
                    <span>{candidate.experience_years} anos de experiência</span>
                  )}
                  <button
                    type="button"
                    onClick={() => setEditingContact(true)}
                    data-testid="edit-contact-btn"
                    className="inline-flex items-center gap-1 text-xs text-brand-blue hover:text-brand-blue-dark font-medium"
                  >
                    <Pencil className="w-3 h-3" />
                    {candidate.email && candidate.phone ? "editar" : "completar contato"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAdditionalInfoOpen(true)}
                    data-testid="open-additional-info-btn"
                    className="inline-flex items-center gap-1 text-xs text-brand-blue hover:text-brand-blue-dark font-medium"
                  >
                    <ClipboardList className="w-3 h-3" />
                    Dados complementares
                  </button>
                </div>
              ) : (
                <div
                  data-testid="contact-edit-form"
                  className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 p-3 bg-blue-50/40 border border-blue-200 rounded-md"
                >
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Nome
                    </label>
                    <Input
                      value={contactDraft.name}
                      onChange={(e) =>
                        setContactDraft({ ...contactDraft, name: e.target.value })
                      }
                      placeholder="Nome do candidato"
                      data-testid="contact-name-input"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      E-mail
                    </label>
                    <Input
                      type="email"
                      value={contactDraft.email}
                      onChange={(e) =>
                        setContactDraft({ ...contactDraft, email: e.target.value })
                      }
                      placeholder="exemplo@email.com"
                      data-testid="contact-email-input"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      Telefone (WhatsApp)
                    </label>
                    <Input
                      type="tel"
                      value={contactDraft.phone}
                      onChange={(e) =>
                        setContactDraft({ ...contactDraft, phone: e.target.value })
                      }
                      placeholder="(11) 99999-1234"
                      data-testid="contact-phone-input"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="sm:col-span-3 flex items-center justify-between gap-2 mt-1">
                    <p className="text-[10px] text-slate-500">
                      💡 Telefone com DDD vira automaticamente <code>+55…</code> para
                      envio via WhatsApp.
                    </p>
                    <div className="flex gap-1.5 shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleCancelEditContact}
                        disabled={savingContact}
                        className="h-7 text-xs"
                        data-testid="contact-cancel-btn"
                      >
                        Cancelar
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveContact}
                        disabled={savingContact}
                        className="h-7 text-xs bg-brand-blue hover:bg-brand-blue-dark text-white"
                        data-testid="contact-save-btn"
                      >
                        {savingContact ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <Save className="w-3 h-3 mr-1" />
                        )}
                        Salvar
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="w-32 shrink-0">
              <ScoreIndicator
                score={candidate.ranking_score ?? candidate.score}
                aiScore={candidate.score}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-4">
            <div className="flex flex-col gap-0.5">
              <label
                htmlFor="status-dropdown"
                className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold"
              >
                Status
              </label>
              <Select value={candidate.status} onValueChange={handleStatusChange} disabled={statusUpdating}>
                <SelectTrigger
                  id="status-dropdown"
                  data-testid="candidate-status-select"
                  className="w-44 bg-white"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Preferred channel selector (iter77) */}
            <div className="flex flex-col gap-0.5">
              <label
                htmlFor="channel-dropdown"
                className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold"
              >
                Canal Maya
              </label>
              <Select
                value={candidate.preferred_channel || "auto"}
                onValueChange={handlePreferredChannelChange}
              >
                <SelectTrigger
                  id="channel-dropdown"
                  data-testid="candidate-channel-select"
                  className="w-44 bg-white"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">🔀 Auto (multi-canal)</SelectItem>
                  <SelectItem value="whatsapp">💬 Somente WhatsApp</SelectItem>
                  <SelectItem value="linkedin">💼 Somente LinkedIn</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {candidate.email_sent && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs">
                <CheckCircle2 className="w-3 h-3" /> E-mail enviado
              </span>
            )}
            {candidate.whatsapp_sent && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs">
                <CheckCircle2 className="w-3 h-3" /> WhatsApp enviado
              </span>
            )}
            {candidate.status === "contratado" && (
              <span
                data-testid="hired-badge"
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-gradient-to-r from-amber-400 to-amber-600 text-white text-xs font-bold shadow-sm"
              >
                <Trophy className="w-3 h-3" /> Contratado
              </span>
            )}
            {!["pendente", "erro", "rejeitado", "contratado"].includes(
              candidate.status,
            ) && (
              <Button
                size="sm"
                onClick={() => setHireDialogOpen(true)}
                data-testid="hire-candidate-button"
                className={
                  candidate.status === "aprovado"
                    ? "h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white shadow-lg shadow-amber-400/50 ring-2 ring-amber-300 ring-offset-1 animate-pulse"
                    : "h-8 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                }
                title="Marcar este candidato como contratado e encerrar o processo"
              >
                <Trophy className="w-3.5 h-3.5 mr-1" />
                Marcar como contratado
              </Button>
            )}

            {/* PDF actions (right-aligned) */}
            <div className="ml-auto flex items-center gap-1.5">
              {candidate.has_pdf ? (
                <>
                  <Button
                    size="sm"
                    variant={showPdf ? "default" : "outline"}
                    onClick={handleTogglePdf}
                    data-testid="toggle-pdf-btn"
                    className={`h-8 text-xs ${
                      showPdf
                        ? "bg-brand-blue hover:bg-brand-blue-dark text-white"
                        : ""
                    }`}
                    title={showPdf ? "Ocultar PDF" : "Ver currículo lado a lado"}
                  >
                    <FileText className="w-3.5 h-3.5 mr-1" />
                    {showPdf ? "Ocultar PDF" : "Ver currículo"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDownloadPdf}
                    data-testid="download-pdf-btn"
                    className="h-8 text-xs"
                    title="Baixar PDF original"
                  >
                    <Download className="w-3.5 h-3.5 mr-1" />
                    Baixar
                  </Button>
                </>
              ) : (
                <span
                  className="inline-flex items-center gap-1.5 text-xs text-slate-400 italic"
                  title="PDF original não disponível para este candidato"
                  data-testid="pdf-unavailable-hint"
                >
                  <FileText className="w-3.5 h-3.5" />
                  PDF não disponível
                </span>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={handleDownloadAdditionalInfoPdf}
                disabled={downloadingAdditionalInfo}
                data-testid="download-additional-info-pdf-btn"
                className="h-8 text-xs"
                title="Baixar PDF com os dados complementares (RG, CPF, CNH, etc.)"
              >
                {downloadingAdditionalInfo ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                ) : (
                  <ClipboardList className="w-3.5 h-3.5 mr-1" />
                )}
                Dados
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Banner: candidato aprovado — orienta a formalizar a contratação */}
        {candidate.status === "aprovado" && (
          <div
            data-testid="approved-next-step-banner"
            className="mx-6 mt-4 bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-300 rounded-md p-3 flex items-start gap-2.5 shadow-sm"
          >
            <Trophy className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1 text-sm text-amber-900 leading-relaxed">
              <strong>Próximo passo: formalize a contratação.</strong>{" "}
              O candidato está aprovado — clique em{" "}
              <strong>🏆 Marcar como contratado</strong> acima para registrar
              salário, data oficial, encerrar a vaga e disparar os agradecimentos
              automáticos. Sem essa etapa, o relatório de fechamento não vai
              contar esta contratação.
            </div>
            <Button
              size="sm"
              onClick={() => setHireDialogOpen(true)}
              data-testid="approved-banner-hire-button"
              className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white shrink-0"
            >
              <Trophy className="w-3 h-3 mr-1" />
              Formalizar agora
            </Button>
          </div>
        )}

        {/* Banner: testes obrigatórios pendentes — visível quando há vaga
            com required_assessments E o candidato ainda não tem assessments
            ativos cobrindo todos. CTA mais forte quando candidato já está
            em entrevista (auto-trigger deveria ter aplicado mas não aplicou). */}
        {missingRequiredTestIds.length > 0 && candidate.status !== "aprovado" && candidate.status !== "contratado" && candidate.status !== "rejeitado" && (
          <div
            data-testid="missing-required-assessments-banner"
            className={`mx-6 mt-4 rounded-md p-3 flex items-start gap-2.5 shadow-sm border ${
              candidate.status === "em_entrevista"
                ? "bg-gradient-to-r from-rose-50 to-rose-100 border-rose-300"
                : "bg-gradient-to-r from-violet-50 to-blue-50 border-violet-200"
            }`}
          >
            <Sparkles className={`w-5 h-5 mt-0.5 shrink-0 ${candidate.status === "em_entrevista" ? "text-rose-600" : "text-violet-600"}`} />
            <div className={`flex-1 text-sm leading-relaxed ${candidate.status === "em_entrevista" ? "text-rose-900" : "text-violet-900"}`}>
              <strong>
                {candidate.status === "em_entrevista"
                  ? `⚠️ Testes obrigatórios pendentes (${missingRequiredTestIds.length})`
                  : `Esta vaga exige ${missingRequiredTestIds.length} teste${missingRequiredTestIds.length > 1 ? "s" : ""} obrigatório${missingRequiredTestIds.length > 1 ? "s" : ""}`}
              </strong>
              <span className="block text-xs mt-0.5 opacity-90">
                {candidate.status === "em_entrevista"
                  ? "A Maya deveria ter aplicado automaticamente. Clique pra forçar a aplicação agora."
                  : "Serão aplicados automaticamente quando o candidato for movido pra Em entrevista. Você também pode aplicar agora."}
              </span>
            </div>
            <Button
              size="sm"
              onClick={handleApplyRequiredNow}
              disabled={applyingRequired}
              data-testid="apply-required-now-button"
              className={`h-7 text-xs text-white shrink-0 ${candidate.status === "em_entrevista" ? "bg-rose-600 hover:bg-rose-700" : "bg-violet-600 hover:bg-violet-700"}`}
            >
              {applyingRequired ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Sparkles className="w-3 h-3 mr-1" />
              )}
              Aplicar agora
            </Button>
          </div>
        )}

        {/* Tabs */}
        <div className="px-6 border-b border-slate-200">
          <div className="flex gap-1 flex-wrap">
            {[
              { id: "analise", label: "Análise da IA" },
              { id: "avaliacoes", label: "Avaliações" },
              { id: "notas", label: `Notas${notes.length ? ` (${notes.length})` : ""}` },
              { id: "gravacoes", label: "Gravações" },
              { id: "agendar", label: "Agendar entrevista" },
              { id: "email", label: "Enviar e-mail", disabled: !candidate.email || !canSendEmail },
              { id: "whatsapp", label: "Enviar WhatsApp", disabled: !candidate.phone || !canSendWA },
              { id: "linkedin", label: "LinkedIn" },
              { id: "historico", label: "Histórico" },
            ].map((t) => (
              <button
                key={t.id}
                disabled={t.disabled}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.id
                    ? "border-brand-blue text-brand-blue"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                } ${t.disabled ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {tab === "analise" && (
            <div className="space-y-5">
              <div>
                <div className="text-xs uppercase tracking-wider font-medium text-slate-500 mb-2">
                  Resumo executivo
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {candidate.summary || "—"}
                </p>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider font-medium text-slate-500 mb-2">
                  Justificativa do score (IA: {candidate.score}%)
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {candidate.justification || "—"}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <div className="text-xs uppercase tracking-wider font-medium text-emerald-700 mb-2">
                    Pontos fortes
                  </div>
                  {candidate.strengths.length === 0 ? (
                    <div className="text-sm text-slate-400">—</div>
                  ) : (
                    <ul className="space-y-1.5">
                      {candidate.strengths.map((s, i) => (
                        <li
                          key={`s-${s.slice(0, 40)}-${i}`}
                          className="text-sm text-slate-700 pl-4 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:rounded-full before:bg-emerald-500"
                        >
                          {s}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider font-medium text-amber-700 mb-2">
                    Pontos de atenção
                  </div>
                  {candidate.gaps.length === 0 ? (
                    <div className="text-sm text-slate-400">—</div>
                  ) : (
                    <ul className="space-y-1.5">
                      {candidate.gaps.map((g, i) => (
                        <li
                          key={`g-${g.slice(0, 40)}-${i}`}
                          className="text-sm text-slate-700 pl-4 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:rounded-full before:bg-amber-500"
                        >
                          {g}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider font-medium text-slate-500 mb-2">
                  Habilidades identificadas
                </div>
                {candidate.skills.length === 0 ? (
                  <div className="text-sm text-slate-400">—</div>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {candidate.skills.map((k, i) => (
                      <span
                        key={i}
                        className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded-md border border-slate-200"
                      >
                        {k}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Manager assignment status */}
              {candidate.assigned_manager_id && (
                <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
                  <UserCheck className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    Encaminhado ao gestor{" "}
                    <strong>{candidate.assigned_manager_name}</strong> para entrevista.
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-slate-100 flex justify-between items-center flex-wrap gap-2">
                <span className="text-xs text-slate-500">
                  Arquivo: {candidate.filename}
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  {canAssign && job?.manager_id && (
                    <Button
                      data-testid="assign-manager-button"
                      onClick={handleAssignManager}
                      disabled={assigning || candidate.assigned_manager_id === job.manager_id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      {assigning ? (
                        <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                      ) : (
                        <UserCheck className="w-4 h-4 mr-1.5" />
                      )}
                      {candidate.assigned_manager_id === job.manager_id
                        ? `Já enviado para ${job.manager_name}`
                        : `Enviar para ${job.manager_name || "gestor"}`}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    data-testid="reanalyze-candidate-button"
                    onClick={handleReanalyze}
                    disabled={reanalyzing || candidate.status === "pendente"}
                  >
                    {reanalyzing ? (
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-1.5" />
                    )}
                    {candidate.status === "pendente" ? "Reanalisando…" : "Reanalisar com IA"}
                  </Button>
                  {canDelete && (
                    <Button
                      variant="ghost"
                      data-testid="delete-candidate-button"
                      onClick={handleDelete}
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4 mr-1.5" />
                      Excluir candidato
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {tab === "avaliacoes" && (
            <CandidateAssessmentsPanel
              candidate={candidate}
              jobId={candidate?.job_id || ""}
            />
          )}

          {tab === "notas" && (
            <div className="space-y-4" data-testid="candidate-notes">
              {/* New note */}
              {canNote ? (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <label className="text-xs font-semibold text-brand-blue uppercase tracking-wide mb-1.5 inline-flex items-center gap-1.5">
                    <StickyNote className="w-3.5 h-3.5" />
                    Adicionar nova nota
                  </label>
                  <Textarea
                    rows={3}
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    data-testid="new-note-input"
                    placeholder="Ex: Entrevista realizada em 28/04. Boa aderência técnica, fluência em inglês conforme pedido."
                    className="bg-white"
                  />
                  <div className="flex justify-end mt-2">
                    <Button
                      size="sm"
                      data-testid="save-note-button"
                      onClick={handleSaveNote}
                      disabled={savingNote || !newNote.trim()}
                      className="bg-brand-blue hover:bg-brand-blue-dark text-white"
                    >
                      {savingNote ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Send className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      Salvar nota
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-500 px-3 py-2">
                  Você não tem permissão para adicionar notas neste candidato.
                </div>
              )}

              {/* Notes list */}
              {loadingNotes ? (
                <div className="flex items-center justify-center py-10 text-slate-500 text-sm">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Carregando notas…
                </div>
              ) : notes.length === 0 ? (
                <div className="text-center py-10 text-sm text-slate-500">
                  <StickyNote className="w-7 h-7 mx-auto mb-2 text-slate-300" />
                  Nenhuma nota registrada ainda. Use o bloco acima para começar.
                </div>
              ) : (
                <ol className="space-y-3">
                  {notes.map((n) => {
                    const isMine = n.user_id === me?.id;
                    const role = (n.user_role_label || "").toLowerCase();
                    const colour =
                      role === "gestor"
                        ? "bg-emerald-50 border-emerald-200"
                        : role === "administrador"
                        ? "bg-violet-50 border-violet-200"
                        : "bg-blue-50 border-blue-200";
                    const badgeColour =
                      role === "gestor"
                        ? "bg-emerald-100 text-emerald-800"
                        : role === "administrador"
                        ? "bg-violet-100 text-violet-800"
                        : "bg-blue-100 text-brand-blue";
                    let when = "";
                    try {
                      when = new Date(n.created_at).toLocaleString("pt-BR", {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      });
                    } catch {
                      when = n.created_at;
                    }
                    return (
                      <li
                        key={n.id}
                        data-testid={`note-${n.id}`}
                        className={`relative border rounded-md p-3.5 ${colour}`}
                      >
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <div
                              className={`shrink-0 w-7 h-7 rounded-full ${badgeColour} flex items-center justify-center text-[11px] font-bold uppercase`}
                            >
                              {(n.user_name || "?").charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-900 truncate">
                                {n.user_name || "Usuário"}
                              </div>
                              <div className="text-[11px] text-slate-500 inline-flex items-center gap-1.5">
                                <span
                                  className={`text-[10px] px-1.5 py-0.5 rounded ${badgeColour} uppercase font-semibold tracking-wide`}
                                >
                                  {n.user_role_label || "—"}
                                </span>
                                <span>{when}</span>
                              </div>
                            </div>
                          </div>
                          {(isMine || me?.role === "admin") && (
                            <button
                              type="button"
                              onClick={() => handleDeleteNote(n.id)}
                              data-testid={`delete-note-${n.id}`}
                              className="text-slate-400 hover:text-red-600 transition-colors"
                              aria-label="Excluir"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap break-words leading-relaxed">
                          {n.body}
                        </p>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          )}

          {tab === "gravacoes" && (
            <CandidateInterviewsPanel candidateId={candidate.id} candidate={candidate} />
          )}

          {tab === "agendar" && (
            <div data-testid="agendar-tab" className="space-y-4">
              <div className="bg-gradient-to-br from-violet-50 via-white to-blue-50 border border-violet-100 rounded-lg p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-md bg-brand-blue text-white flex items-center justify-center shrink-0">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="heading text-base font-bold text-slate-900 mb-1">
                      Agendar entrevista com {candidate.name || "este candidato"}
                    </h3>
                    <p className="text-sm text-slate-600 mb-4 leading-snug">
                      Vamos sugerir os próximos horários disponíveis na agenda do
                      gestor. A sala de vídeo é criada automaticamente — você só
                      precisa acessar no dia da entrevista.
                    </p>
                    <Button
                      data-testid="open-schedule-from-candidate"
                      onClick={() => setScheduleOpen(true)}
                      className="bg-brand-blue hover:bg-brand-blue-dark text-white"
                    >
                      <Sparkles className="w-4 h-4 mr-1.5" />
                      Iniciar agendamento
                    </Button>
                  </div>
                </div>
              </div>

              {/* Existing interviews for this candidate */}
              <CandidateInterviewsPanel candidateId={candidate.id} candidate={candidate} />
            </div>
          )}

          {tab === "email" && (
            <div className="space-y-4">
              {!candidate.email ? (
                <div className="flex items-start gap-3 px-4 py-4 rounded-md bg-amber-50 border border-amber-200 text-amber-800">
                  <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">
                      E-mail não detectado no currículo.
                    </p>
                    <p className="text-xs mt-1 text-amber-700">
                      Adicione manualmente para liberar o envio de e-mail.
                    </p>
                    <Button
                      size="sm"
                      onClick={() => setEditingContact(true)}
                      className="mt-2 h-8 bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="add-email-from-tab"
                    >
                      <Pencil className="w-3 h-3 mr-1.5" />
                      Adicionar e-mail
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-500">Para:</span>
                    <span className="font-medium text-slate-900">{candidate.email}</span>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Assunto
                    </label>
                    <Input
                      data-testid="email-subject-input"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Mensagem
                    </label>
                    <Textarea
                      data-testid="email-body-input"
                      rows={12}
                      value={emailBody}
                      onChange={(e) => setEmailBody(e.target.value)}
                    />
                  </div>

                  <div className="flex justify-end pt-2">
                    <Button
                      data-testid="send-email-button"
                      onClick={handleSendEmail}
                      disabled={sending || !emailSubject.trim() || !emailBody.trim()}
                      className="bg-brand-blue hover:bg-brand-blue-dark text-white"
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Enviar e-mail
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "whatsapp" && (
            <div className="space-y-4">
              {!candidate.phone ? (
                <div className="flex items-start gap-3 px-4 py-4 rounded-md bg-amber-50 border border-amber-200 text-amber-800">
                  <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">
                      Telefone não detectado no currículo.
                    </p>
                    <p className="text-xs mt-1 text-amber-700">
                      Você pode adicionar manualmente para liberar o envio de WhatsApp.
                    </p>
                    <Button
                      size="sm"
                      onClick={() => setEditingContact(true)}
                      className="mt-2 h-8 bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="add-phone-from-tab"
                    >
                      <Pencil className="w-3 h-3 mr-1.5" />
                      Adicionar telefone
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                    <MessageCircle className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-500">Para:</span>
                    <span className="font-medium text-slate-900">{candidate.phone}</span>
                  </div>

                  <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3 leading-relaxed">
                    A mensagem de boas-vindas é gerada automaticamente com base
                    no perfil da vaga e enviada via <strong>Synkly</strong>.
                    Configure sua conta WhatsApp em{" "}
                    <strong>Configurações → Synkly</strong>.
                  </div>

                  <div className="flex justify-end items-center pt-2">
                    <Button
                      data-testid="send-wa-button"
                      onClick={handleSendWhatsapp}
                      disabled={sending}
                      className="bg-brand-blue hover:bg-brand-blue-dark text-white"
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Enviar WhatsApp
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "linkedin" && (
            <CandidateLinkedInTab
              candidate={candidate}
              job={job}
              onUpdated={onUpdated}
            />
          )}

          {tab === "historico" && (
            <div className="space-y-3" data-testid="candidate-timeline">
              {loadingTimeline ? (
                <div className="flex items-center justify-center py-12 text-slate-500 text-sm">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Carregando histórico…
                </div>
              ) : timeline.length === 0 ? (
                <div className="text-center py-12 text-sm text-slate-500">
                  <Clock className="w-6 h-6 mx-auto mb-2 text-slate-300" />
                  Nenhuma comunicação registrada ainda.
                </div>
              ) : (
                <ol className="relative border-l-2 border-slate-100 ml-2 space-y-5 pl-5">
                  {timeline.map((evt) => {
                    const isInbound = evt.direction === "inbound";
                    const isEmail = evt.channel === "email";
                    const dotClass = isInbound
                      ? "bg-emerald-100 text-emerald-700 ring-emerald-200"
                      : isEmail
                      ? "bg-blue-50 text-brand-blue ring-blue-200"
                      : "bg-slate-100 text-slate-700 ring-slate-200";
                    const Icon = isEmail
                      ? Mail
                      : isInbound
                      ? ArrowDownLeft
                      : ArrowUpRight;
                    let when = "";
                    try {
                      when = new Date(evt.created_at).toLocaleString("pt-BR", {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      });
                    } catch {
                      when = evt.created_at;
                    }
                    return (
                      <li key={evt.id} className="relative">
                        <span
                          className={`absolute -left-[34px] top-0 w-7 h-7 rounded-full ring-4 ring-white flex items-center justify-center ${dotClass}`}
                        >
                          <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
                        </span>
                        <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                          <div className="flex items-center gap-2 flex-wrap mb-1.5">
                            <span className="text-xs font-medium text-slate-700 uppercase tracking-wide">
                              {isEmail ? "E-mail" : "WhatsApp"}
                            </span>
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded ${
                                isInbound
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-blue-50 text-brand-blue"
                              }`}
                            >
                              {isInbound ? "recebida" : "enviada"}
                            </span>
                            {evt.auto_reply && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">
                                auto-resposta
                              </span>
                            )}
                            <span className="text-xs text-slate-500 ml-auto">{when}</span>
                          </div>
                          {evt.subject && (
                            <div className="text-sm font-medium text-slate-900 mb-1">
                              {evt.subject}
                            </div>
                          )}
                          <div className="text-sm text-slate-700 whitespace-pre-wrap break-words">
                            {(evt.body || "").length > 360
                              ? `${evt.body.slice(0, 360)}…`
                              : evt.body}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          )}
        </div>
          </div>

          {showPdf && (
            <div
              data-testid="pdf-viewer-panel"
              className="w-[55%] min-w-[420px] bg-slate-100 flex flex-col"
            >
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-white">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-4 h-4 text-brand-blue shrink-0" />
                  <span className="text-sm font-medium text-slate-800 truncate">
                    Currículo original
                  </span>
                  <span className="text-xs text-slate-500 truncate">
                    · {candidate.filename || "PDF"}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleDownloadPdf}
                    data-testid="pdf-download-inner-btn"
                    className="h-8 text-xs"
                  >
                    <Download className="w-3.5 h-3.5 mr-1" />
                    Baixar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowPdf(false)}
                    data-testid="pdf-close-btn"
                    className="h-8 text-xs"
                    aria-label="Fechar PDF"
                  >
                    <PanelRightClose className="w-3.5 h-3.5 mr-1" />
                    Ocultar
                  </Button>
                </div>
              </div>
              <div className="flex-1 relative bg-slate-200">
                {pdfLoading && (
                  <div className="absolute inset-0 flex items-center justify-center gap-2 text-slate-600">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Carregando PDF…</span>
                  </div>
                )}
                {!pdfLoading && pdfError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
                    <AlertCircle className="w-10 h-10 text-amber-500 mb-2" />
                    <p className="text-sm text-slate-700 max-w-xs">{pdfError}</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={loadPdfPreviewUrl}
                      className="mt-3 text-xs"
                    >
                      Tentar novamente
                    </Button>
                  </div>
                )}
                {!pdfLoading && !pdfError && pdfPreviewUrl && (
                  // Use a direct signed URL (?t=token) in the iframe so the
                  // browser's native PDF viewer works without blob URLs, which
                  // are blocked in nested iframes (Edge / Replit preview).
                  <iframe
                    src={pdfPreviewUrl}
                    title="Currículo PDF"
                    data-testid="pdf-iframe"
                    className="w-full h-full border-0"
                  />
                )}
              </div>
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          data-testid="close-candidate-modal"
          className="absolute right-4 top-4 text-slate-400 hover:text-slate-700 p-1 rounded-md hover:bg-slate-100"
          aria-label="Fechar"
        >
          <X className="w-4 h-4" />
        </button>
      </DialogContent>

      <ScheduleInterviewDialog
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        jobId={job?.id || candidate.job_id}
        candidate={candidate}
        defaultManagerId={job?.manager_id || ""}
        onScheduled={(interview, room) => {
          setScheduleOpen(false);
          setScheduleSuccess({ interview, room });
        }}
      />

      <InterviewSuccessDialog
        open={!!scheduleSuccess}
        onClose={() => setScheduleSuccess(null)}
        interview={scheduleSuccess?.interview}
        room={scheduleSuccess?.room}
        candidateId={candidate.id || ""}
        candidateName={candidate.name || ""}
        candidatePhone={candidate.phone || ""}
        jobTitle={job?.title || ""}
        onCompleteInfo={() => {
          setScheduleSuccess(null);
          setAdditionalInfoOpen(true);
        }}
      />

      <CandidateAdditionalInfoDialog
        open={additionalInfoOpen}
        onClose={() => setAdditionalInfoOpen(false)}
        candidateId={candidate.id || ""}
        onSaved={(updated) => {
          if (typeof onUpdated === "function") onUpdated(updated);
        }}
      />

      <HireCandidateDialog
        open={hireDialogOpen}
        onClose={() => setHireDialogOpen(false)}
        candidate={candidate}
        jobTitle={job?.title || ""}
        onHired={() => {
          // Reload candidate state so the badge updates immediately
          if (typeof onUpdated === "function") onUpdated({ ...candidate, status: "contratado" });
        }}
      />
    </Dialog>
  );
};

export default CandidateModal;
